import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Modal,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import React, { useState } from 'react'
import { FieldConfig, KindOption } from '../EditNodes'
import { JSONSchema7 } from 'json-schema'
import { getHeaders } from '../../utils/common'
import { UiSchema } from '@rjsf/core'

interface Props {
  isModalOpen: boolean
  setIsModalOpen: (isOpen: boolean) => void
  sessions: any
  selectedOption: string
  setKindOptions: (options: KindOption[] | null) => void
}

type ConfigInfo = {
  schema: JSONSchema7
  uiSchema: UiSchema
}

/**
 * AddNewNodeModal Component
 * Displays a modal window with a centered content box
 */
const AddNewNodeModal: React.FC<Props> = ({ isModalOpen, setIsModalOpen, sessions, selectedOption, setKindOptions }) => {
  // const dispatch = useAppDispatch()
  const [configInfo, setConfigInfo] = useState<ConfigInfo | null>(null)
  const [nodeName, setNodeName] = useState<string>('')
  const [nodeCategory, setNodeCategory] = useState<string>('')
  const [errorMessage, setErrorMessage] = useState<string>('')

  const [fieldName, setFieldName] = useState('')
  const [fieldType, setFieldType] = useState('string')
  const [fieldDescription, setFieldDescription] = useState('')
  const [fieldDefault, setFieldDefault] = useState('')
  const [errorMessageForField, setErrorMessageForField] = useState('')
  const [newFields, setNewFields] = useState<FieldConfig[]>([])
  const [fieldRequired, setFieldRequired] = useState(false)
  const [isFieldAdd, setIsFieldAdd] = useState<boolean>(false)

  const supportedTypes = ['boolean', 'string', 'number']

  const validateField = () => {
    if (!fieldName.trim()) {
      setErrorMessageForField('Field name is required.')
      return false
    }
    if (!supportedTypes.includes(fieldType)) {
      setErrorMessageForField(`Unsupported field type '${fieldType}'. Supported types are: ${supportedTypes.join(', ')}.`)
      return false
    }
    setErrorMessageForField('')
    return true
  }

  const handleSave = async () => {
    if (!nodeName || !nodeCategory) {
      setErrorMessage('Both fields are required!')
      return
    }

    try {
      const sessionId = sessions[0]?.id
      const tmpInfo: ConfigInfo = {
        schema: {},
        uiSchema: {},
      }

      const response = await fetch(`/mqttapi/v1/session/${sessionId}/list/${selectedOption}`, {
        headers: {
          ...getHeaders(),
          'Content-Type': 'application/json',
        },
        method: 'POST',
        body: JSON.stringify({
          name: nodeName,
          schema: tmpInfo,
          categories: [nodeCategory],
        }),
      })

      if (!response.ok) {
        const errorText = await response.text()
        // dispatch(showAlert('error', `Error adding node: ${errorText}`, true))
        console.log(`Error adding node: ${errorText}`);
        return
      }

      // dispatch(showAlert('success', 'Node added successfully!', true))
      
      getConfigData(nodeName)

      if (sessionId && selectedOption) {
        fetch(`/mqttapi/v1/session/${sessionId}/list/${selectedOption}`, {
          headers: getHeaders(),
          method: 'GET',
        })
          .then(async (response) => {
            if (!response.ok) {
              const text = await response.text()
              // dispatch(showAlert('error', `Error fetching list: ${text}`, true))
              console.log(`Error fetching list: ${text}`);
              return
            }
            return response.json()
          })
          .then((data) => {
            if (Array.isArray(data)) {
              setKindOptions(data)
            } else {
              console.log(`Unexpected response format: ${JSON.stringify(data)}`);
              
              // dispatch(showAlert('error', `Unexpected response format: ${JSON.stringify(data)}`, true))
            }
          })
          .catch((error) => {
            console.log(`Error fetching data: ${error.message}`);
            
            // dispatch(showAlert('error', `Error fetching data: ${error.message}`, true))
          })
      }
    } catch (error: any) {
      console.log(`Error updating config: ${error.message}`);
      
      // dispatch(showAlert('error', `Error updating config: ${error.message}`, true))
    }

    setNodeCategory('')
    setErrorMessage('')
  }

  const getConfigData = (nodeName: string) => {
    setIsFieldAdd(true)
    const sessionId = sessions[0]?.id

    if (!sessionId) {
      return
    }

    let subscribed = true

    fetch(`/mqttapi/v1/session/${sessionId}/config/${selectedOption}/${nodeName}`, {
      headers: getHeaders(),
      method: 'GET',
    })
      .then(async (response) => {
        if (response.ok) {
          return response.json()
        }
        const errorText = await response.text()
        throw new Error(`${response.status}: ${response.statusText}: ${errorText}`)
      })
      .then((json) => {
        if (!subscribed) return

        // Normalize schema properties
        const normalizedSchema: JSONSchema7 = {
          type: json.schema?.type || 'object',
          properties: json.schema?.properties || {},
          required: json.schema?.required || [],
          additionalProperties: typeof json.schema?.additionalProperties === 'boolean' ? json.schema.additionalProperties : false,
        }

        const normalizedUiSchema = {
          ...json.uiSchema,
        }

        const tmpInfo: ConfigInfo = {
          schema: normalizedSchema,
          uiSchema: normalizedUiSchema,
        }

        setConfigInfo(tmpInfo)
      })
      .catch((error) => {
        console.error('Error fetching config:', error)
      })

    return () => {
      subscribed = false
    }
  }

  const handleAddField = () => {
    if (!validateField()) {
      console.log("Validation failed. Please check the field details.");
      
      // dispatch(showAlert('error', 'Validation failed. Please check the field details.', true))
      return
    }

    const parsedDefault =
      fieldType === 'boolean'
        ? fieldDefault.toLowerCase() === 'true'
        : fieldType === 'number'
        ? Number(fieldDefault)
        : fieldType === 'string'
        ? String(fieldDefault)
        : fieldDefault

    const newField: FieldConfig = {
      name: fieldName,
      type: fieldType,
      description: fieldDescription,
      default: parsedDefault,
      required: fieldRequired,
    }

    setNewFields((prevFields: FieldConfig[]) => [...prevFields, newField])
    setFieldName('')
    setFieldType('string')
    setFieldDescription('')
    setFieldDefault('')
    setFieldRequired(false)
    console.log('Field added successfully.');
    
    // dispatch(showAlert('success', 'Field added successfully.', true))
  }

  const handleRemoveField = (fieldName: string, isFromConfig: boolean) => {
    if (isFromConfig && configInfo?.schema) {
      const updatedSchema = { ...configInfo.schema }
      const updatedUiSchema = { ...configInfo.uiSchema }

      // Ensure properties exist
      updatedSchema.properties = updatedSchema.properties || {}

      // Remove the field from schema properties
      if (updatedSchema.properties[fieldName]) {
        delete updatedSchema.properties[fieldName]
      }

      // Update the 'required' array safely
      if (updatedSchema.required) {
        updatedSchema.required = updatedSchema.required.filter((reqField) => reqField !== fieldName)
      }

      // Remove the field from UI schema order
      if (updatedUiSchema['ui:order']) {
        updatedUiSchema['ui:order'] = updatedUiSchema['ui:order'].filter((orderField:any) => orderField !== fieldName)
      }

      // Update config info
      setConfigInfo({
        ...configInfo,
        schema: updatedSchema,
        uiSchema: updatedUiSchema,
      })
    } else {
      // Remove the field from newFields for local changes
      setNewFields((prevFields) => prevFields.filter((field) => field.name !== fieldName))
    }
  }

  const handleSaveFields = async () => {
    if (configInfo) {
      const updatedSchema = { ...configInfo.schema }
      const updatedUiSchema = { ...configInfo.uiSchema }

      newFields.forEach((field: any) => {
        if (!updatedSchema.properties) updatedSchema.properties = {}
        if (!updatedSchema.required) updatedSchema.required = []
        if (!updatedUiSchema['ui:order']) updatedUiSchema['ui:order'] = []

        // Update schema properties
        updatedSchema.properties[field.name] = {
          type: field.type,
          description: field.description,
          default: field.default,
        }

        // Mark field as required if specified
        if (field.required) {
          updatedSchema.required.push(field.name)
        }

        // Update UI schema order
        updatedUiSchema['ui:order'].push(field.name)
      })

      configInfo.schema = updatedSchema
      configInfo.uiSchema = updatedUiSchema

      try {
        const sessionId = sessions[0]?.id
        const response = await fetch(`/mqttapi/v1/session/${sessionId}/config/${selectedOption}/${nodeName}`, {
          headers: {
            ...getHeaders(),
            'Content-Type': 'application/json',
          },
          method: 'POST',
          body: JSON.stringify(configInfo),
        })

        if (!response.ok) {
          throw new Error(`Failed to update config: ${response.statusText}`)
        }
        // dispatch(showAlert('success', 'Fields saved successfully.', true))
        console.log('Fields saved successfully.');
      } catch (error) {
        console.error('Error updating config:', error)
        // dispatch(showAlert('error', 'Failed to save fields. Please try again.', true))
      }

      setNodeName('')
      setNewFields([]);
      setIsFieldAdd(false);
      setIsModalOpen(false);
    }
  }

  return (
    <Modal open={isModalOpen} onClose={() => setIsModalOpen(false)}>
      <Box
        sx={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          bgcolor: 'white',
          p: 4,
          borderRadius: 2,
          width: { xs: '95%', sm: '80%' },
          maxHeight: '85vh',
          overflowY: 'auto',
          boxShadow: 3,
          display: 'flex',
          flexDirection: 'column',
          justifyContent:"space-between",
          gap: 2,
          height: isFieldAdd ? '70vh' : '40vh', 
          transition: 'height 0.3s ease, transform 0.3s ease',
        }}
      >
        {!isFieldAdd ? (
          <>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Add New Node
              </Typography>
              <TextField label="Field Name" sx={{ mb: 2 }} value={nodeName} onChange={(e) =>{ setNodeName(e.target.value); setErrorMessage('')}} fullWidth />
              <TextField label="Category" value={nodeCategory} onChange={(e) => {setNodeCategory(e.target.value);  setErrorMessage('')}} fullWidth sx={{ mb: 2 }} />
              {errorMessage && (
                <Typography variant="body2" color="error" sx={{ mt: 2 }}>
                  {errorMessage}
                </Typography>
              )}
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              <Button variant="contained" color="secondary" onClick={handleSave}>
                Save & Add fields
              </Button>
              <Button variant="contained" onClick={() =>{ setIsModalOpen(false); setErrorMessage('')}}>
                Cancel
              </Button>
            </Box>
          </>
        ) : (
          <>
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column-reverse', sm: 'row' },
                gap: 2,
              }}
            >
              <Box
                sx={{
                  flex: 1,
                  borderRight: { sm: '1px solid black' },
                  pr: { sm: 2 },
                  maxHeight: { xs: '50vh', sm: '70vh' },
                  overflowY: 'auto',
                }}
              >
                <Typography variant="h6">List of Fields</Typography>
                <TableContainer component={Paper} style={{ marginTop: '20px' }}>
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>
                          <strong>Name</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Type</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Default Value</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Action</strong>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {/* Render existing schema fields */}
                      {configInfo?.schema?.properties && Object.entries(configInfo.schema.properties).length > 0
                        ? Object.entries(configInfo.schema.properties).map(([fieldName, fieldConfig]: [string, any]) => (
                            <TableRow key={fieldName}>
                              <TableCell>{fieldName}</TableCell>
                              <TableCell>{fieldConfig.type || 'N/A'}</TableCell>
                              <TableCell>{fieldConfig.default?.toString() || 'None'}</TableCell>
                              <TableCell>
                                <Button variant="outlined" color="error" size="small" onClick={() => handleRemoveField(fieldName, true)}>
                                  Remove
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))
                        : null}

                      {/* Render newly added fields */}
                      {newFields.length > 0
                        ? newFields.map((field) => (
                            <TableRow key={field.name}>
                              <TableCell>{field.name}</TableCell>
                              <TableCell>{field.type}</TableCell>
                              <TableCell>{field.default?.toString() || 'None'}</TableCell>
                              <TableCell>
                                <Button variant="outlined" color="error" size="small" onClick={() => handleRemoveField(field.name, false)}>
                                  Remove
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))
                        : null}

                      {/* Fallback message if no fields exist */}
                      {Object.entries(configInfo?.schema?.properties || {}).length === 0 && newFields.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={4} align="center">
                            No fields available
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Add New Fields
                </Typography>
                <TextField label="Field Name" sx={{ mb: 2 }} value={fieldName} onChange={(e) => setFieldName(e.target.value)} fullWidth />
                <TextField
                  label="Field Type"
                  value={fieldType}
                  onChange={(e) => setFieldType(e.target.value)}
                  select
                  fullWidth
                  helperText={`Supported types: ${supportedTypes.join(', ')}`}
                  sx={{ mb: 2 }}
                >
                  {supportedTypes.map((type) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  label="Field Description"
                  value={fieldDescription}
                  onChange={(e) => setFieldDescription(e.target.value)}
                  fullWidth
                  sx={{ mb: 2 }}
                />
                <TextField label="Field Default Value" value={fieldDefault} onChange={(e) => setFieldDefault(e.target.value)} fullWidth />
                <FormControlLabel
                  control={<Checkbox checked={fieldRequired} onChange={(e) => setFieldRequired(e.target.checked)} />}
                  label="Required"
                />
                {errorMessageForField && (
                  <Typography variant="body2" color="error" sx={{ mt: 2 }}>
                    {errorMessageForField}
                  </Typography>
                )}
                <Box sx={{ width: '100%', display: 'flex', justifyContent: 'flex-end' }}>
                  <Button variant="contained" color="primary" onClick={handleAddField}>
                    Add Field
                  </Button>
                </Box>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              <Button variant="contained" color="secondary" onClick={handleSaveFields}>
                Save
              </Button>
              <Button variant="contained" onClick={() => setIsModalOpen(false)}>
                Cancel
              </Button>
            </Box>
          </>
        )}
      </Box>
    </Modal>
  )
}

export default AddNewNodeModal
